import React, { useEffect, useMemo, useState } from "react";
import { Activity, Calendar, DollarSign, Plus, TrendingUp } from "lucide-react";
import JeaCard from "@/components/jea/JeaCard";
import JeaButton from "@/components/jea/JeaButton";
import Skeleton from "@/components/jea/Skeleton";
import { demoClient, useDemo } from "@/pages/demo/DemoShell";
import { formatMoney } from "@/lib/pricing";
import { cn } from "@/lib/utils";

const kindLabel = (k) => ({
  filing_fee:  "Filing Fee",
  service_fee: "Service Fee",
  attorney_fee:"Attorney Fee",
  recording:   "Recording",
}[k] || (k || "").replace("_", " "));

const deadlineLabel = (k) => ({
  next_query:       "Next scheduled query",
  judgment_renewal: "Judgment renewal",
}[k] || k);

const shortDate = (iso) => {
  if (!iso) return "—";
  try { return new Date(iso).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" }); }
  catch { return "—"; }
};

const DemoMonitoringPage = () => {
  const demo = useDemo();
  const [data, setData] = useState({ snapshot: {}, costs: [], deadlines: [] });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await demoClient.get("/demo/monitoring/summary");
        setData(data);
      } finally { setLoading(false); }
    })();
  }, []);

  const nudge = (feature) => () => demo?.openSignUp(feature);

  const timeline = useMemo(() => {
    // Simple visual: horizontal bar with dots at each deadline (0-90d range)
    const items = (data.deadlines || []).filter((d) => d.days_away >= 0 && d.days_away <= 90);
    return items.map((d) => ({
      ...d,
      left_pct: Math.min(95, Math.max(2, (d.days_away / 90) * 95 + 2)),
    }));
  }, [data.deadlines]);

  return (
    <div className="space-y-6" data-testid="demo-monitoring-page">
      <div>
        <div className="jea-caption">Portfolio Monitoring</div>
        <h1 className="text-[28px] font-bold text-white">Monitoring</h1>
        <p className="text-text-secondary text-[14px] mt-1">
          Never miss a renewal. Track interest accrual and recoverable costs across your entire portfolio.
        </p>
      </div>

      {/* Snapshot */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <StatTile label="Judgments Monitored" value={data.snapshot?.total_debtors ?? 0} Icon={Activity} />
        <StatTile label="Deadlines · 30d"   value={data.snapshot?.upcoming_deadlines_30d ?? 0} Icon={Calendar} accent />
        <StatTile label="Recoverable Costs" value={formatMoney(data.snapshot?.recoverable_total_cents ?? 0)} Icon={DollarSign} money />
        <StatTile label="Interest Accruing" value={formatMoney(2_450_000)} Icon={TrendingUp} success />
      </div>

      {/* Timeline */}
      <JeaCard data-tour="monitoring-timeline">
        <div className="flex items-center justify-between mb-3">
          <h2 className="text-white font-bold text-[16px]">Deadline Timeline · Next 90 days</h2>
          <span className="text-text-secondary text-[12px]">{timeline.length} events</span>
        </div>
        {loading ? <Skeleton className="h-24" /> : timeline.length === 0 ? (
          <div className="text-text-secondary text-[13px]">No deadlines in the next 90 days.</div>
        ) : (
          <div className="relative h-24">
            {/* baseline */}
            <div className="absolute top-1/2 left-0 right-0 h-[2px] bg-border-default rounded-full" />
            {/* month markers */}
            {[0, 30, 60, 90].map((d) => (
              <div key={d} className="absolute top-1/2 -translate-y-1/2 flex flex-col items-center" style={{ left: `${(d / 90) * 95 + 2}%` }}>
                <div className="w-[1px] h-3 bg-border-default" />
                <div className="text-text-muted text-[10px] jea-mono mt-1">+{d}d</div>
              </div>
            ))}
            {timeline.map((d, i) => (
              <div
                key={i}
                className="absolute top-1/2 -translate-y-1/2 -translate-x-1/2 group"
                style={{ left: `${d.left_pct}%` }}
                data-testid={`demo-deadline-dot-${i}`}
              >
                <div className={cn(
                  "w-3 h-3 rounded-full border-2 transition-transform group-hover:scale-125",
                  d.days_away <= 14 ? "bg-danger border-danger" :
                  d.days_away <= 45 ? "bg-warning border-warning" :
                  "bg-accent-pink border-accent-pink"
                )} />
                <div className="absolute bottom-full mb-2 left-1/2 -translate-x-1/2 whitespace-nowrap bg-bg-primary border border-border-default rounded-[6px] px-2 py-1 text-[11px] text-white opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none">
                  <div className="font-semibold">{d.debtor_name}</div>
                  <div className="text-text-secondary text-[10px]">{deadlineLabel(d.kind)} · {shortDate(d.date)}</div>
                </div>
              </div>
            ))}
          </div>
        )}
      </JeaCard>

      {/* Two-column: costs + deadlines list */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <JeaCard>
          <div className="flex items-center justify-between mb-3">
            <h2 className="text-white font-bold text-[16px]">Recoverable Costs</h2>
            <JeaButton size="sm" className="gap-2" onClick={nudge("adding recoverable costs")} data-testid="demo-monitoring-add-cost">
              <Plus size={14} /> Add Cost
            </JeaButton>
          </div>
          {loading ? <Skeleton className="h-40" /> : data.costs.length === 0 ? (
            <div className="text-text-secondary text-[13px]">No costs logged yet.</div>
          ) : (
            <div className="space-y-2" data-testid="demo-monitoring-costs">
              {data.costs.map((c) => (
                <div key={c.id} className="grid grid-cols-[1fr_auto_auto] items-center gap-3 bg-bg-tertiary rounded-[8px] p-3">
                  <div className="min-w-0">
                    <div className="text-white text-[13px] font-semibold truncate">{c.description}</div>
                    <div className="text-text-muted text-[11px]">{kindLabel(c.kind)} · {shortDate(c.date)}</div>
                  </div>
                  <span className="jea-mono text-accent-pink font-bold text-[13px]">
                    {formatMoney(c.amount_cents || 0)}
                  </span>
                </div>
              ))}
            </div>
          )}
        </JeaCard>

        <JeaCard>
          <h2 className="text-white font-bold text-[16px] mb-3">Upcoming Deadlines</h2>
          {loading ? <Skeleton className="h-40" /> : (data.deadlines || []).length === 0 ? (
            <div className="text-text-secondary text-[13px]">Nothing upcoming.</div>
          ) : (
            <div className="space-y-2" data-testid="demo-monitoring-deadlines">
              {(data.deadlines || []).slice(0, 8).map((d, i) => (
                <div key={i} className="grid grid-cols-[1fr_auto] items-center gap-3 bg-bg-tertiary rounded-[8px] p-3">
                  <div className="min-w-0">
                    <div className="text-white text-[13px] font-semibold truncate">{d.debtor_name}</div>
                    <div className="text-text-muted text-[11px]">{deadlineLabel(d.kind)}</div>
                  </div>
                  <div className="text-right">
                    <div className="jea-date text-[12px]">{shortDate(d.date)}</div>
                    <div className={cn(
                      "text-[11px] font-bold tracking-[0.06em] uppercase",
                      d.days_away <= 14 ? "text-danger" :
                      d.days_away <= 45 ? "text-warning" :
                      "text-accent-pink"
                    )}>
                      In {d.days_away} days
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </JeaCard>
      </div>
    </div>
  );
};

const StatTile = ({ label, value, Icon, accent, money, success }) => (
  <JeaCard>
    <div className="flex items-center justify-between">
      <div className="jea-caption">{label}</div>
      {Icon && <Icon size={14} className="text-text-muted" />}
    </div>
    <div className={cn(
      "jea-mono font-extrabold text-[24px] mt-2",
      money && "text-accent-pink",
      accent && "text-white",
      success && "text-success",
      !money && !success && !accent && "text-white"
    )}>
      {value}
    </div>
  </JeaCard>
);

export default DemoMonitoringPage;
